import type React from 'react'
import { useEffect, useState } from 'react'
import { CookieBanner } from '@/landing/components/cookie_banner'
import DisclaimerModal from '@/landing/components/DisclaimerModal' // Import the new modal
import { Footer, Header, MobileNavigation } from '@/landing/components/navigation'
import type { LandingLayoutProps } from '@/types/components'
import { ActiveSectionProvider } from '../contexts/ActiveSectionContext'
import { CookieConsentProvider, useCookieConsent } from '../contexts/CookieConsentContext' // Import the hook
import { UnitsProvider } from '../contexts/UnitsContext'

const AppContent: React.FC<LandingLayoutProps> = ({
  children,
  currentPath,
  currentUser,
  locale,
  translations,
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const { hasConsent, isLoading } = useCookieConsent()
  const [isDisclaimerOpen, setDisclaimerOpen] = useState(false)

  useEffect(() => {
    if (isLoading) {
      return
    }

    const disclaimerAcknowledged = sessionStorage.getItem('disclaimerAcknowledged')
    if (hasConsent && !disclaimerAcknowledged) {
      setDisclaimerOpen(true)
    }
  }, [hasConsent, isLoading])

  const handleAcknowledge = () => {
    sessionStorage.setItem('disclaimerAcknowledged', 'true')
    setDisclaimerOpen(false)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header
        currentPath={currentPath}
        currentUser={currentUser}
        onMobileMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      />

      <MobileNavigation
        isOpen={isMobileMenuOpen}
        currentPath={currentPath}
        onClose={() => setIsMobileMenuOpen(false)}
      />

      <main className="flex-1">{children}</main>

      <Footer
        currentPath={currentPath}
        currentUser={currentUser}
        csrfToken=""
        controller=""
        action=""
        locale={locale}
        translations={translations}
      />

      <CookieBanner />
      <DisclaimerModal isOpen={isDisclaimerOpen} onAcknowledge={handleAcknowledge} />
    </div>
  )
}

const LandingLayout: React.FC<LandingLayoutProps> = (props) => {
  return (
    <ActiveSectionProvider>
      <CookieConsentProvider>
        <UnitsProvider>
          <AppContent {...props} />
        </UnitsProvider>
      </CookieConsentProvider>
    </ActiveSectionProvider>
  )
}

export default LandingLayout
